import { useEffect, useState } from 'react'
import { Box } from '@mantine/core'
import { resolveVizStyle } from './registry'
import { shiftHue } from './vizColor'

/**
 * The speech visualizer mounted next to a speaking portrait. Picks a style by
 * key (the author-/story-choosable setting), resolves a trace color from the
 * live theme accent (--bw-accent, hue-shifted to a complementary-but-contrasting
 * hue so it pops in every theme), and frames it in a control-panel bezel.
 *
 * All styles are driven by the viseme stream (see useVisemeEnvelope) — the only
 * speech signal that survives our CORS setup — so they animate identically to
 * lip-sync, work on cache hits, and need no audio-graph wiring.
 *
 * @param {object} props
 * @param {object} props.character   the speaking character
 * @param {string} [props.styleKey]  which visualizer style ('scope'|'bars'|...)
 * @param {number} [props.height]    screen height in px
 * @param {boolean} [props.framed]   render the bezel/screen chrome (default true)
 */
export function SpeechVisualizer({ character, styleKey, height = 56, framed = true }) {
  const { Component } = resolveVizStyle(styleKey)
  const color = useAccentTrace()

  const screen = (
    <Box
      style={{
        position: 'relative',
        height,
        borderRadius: framed ? 3 : 6,
        overflow: 'hidden',
        background: framed
          ? 'radial-gradient(ellipse at center, rgba(8,12,18,0.96) 0%, rgba(2,4,7,0.98) 100%)'
          : 'transparent',
        boxShadow: framed
          ? 'inset 0 0 18px rgba(0,0,0,0.85), inset 0 0 2px var(--bw-accent)'
          : 'none',
      }}
    >
      {/* anyVoice: the hero visualizer animates for ANY voice currently
          speaking — including the narrator, who has no character id. */}
      <Component character={character} color={color} anyVoice />
      {framed && (
        <Box
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            backgroundImage:
              'repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 2px, rgba(0,0,0,0.16) 3px)',
            boxShadow: 'inset 0 0 24px rgba(0,0,0,0.6)',
          }}
        />
      )}
    </Box>
  )

  if (!framed) return screen

  return (
    <Box
      style={{
        position: 'relative',
        borderRadius: 6,
        padding: 4,
        background: 'linear-gradient(160deg, rgba(40,46,54,0.95), rgba(14,18,24,0.95))',
        border: '1px solid color-mix(in srgb, var(--bw-accent) 35%, #000)',
        boxShadow:
          'inset 0 1px 0 rgba(255,255,255,0.06), inset 0 0 12px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.5)',
      }}
    >
      {screen}
    </Box>
  )
}

// Reads the live --bw-accent off <html> and returns the hue-shifted trace color.
// Re-reads when the theme changes (ThemedApp toggles data-bw-motion on the root
// alongside the vars, so we observe that attribute as a cheap change signal).
function useAccentTrace() {
  const [color, setColor] = useState('rgba(255, 92, 222, 1)')

  useEffect(() => {
    const read = () => {
      const root = document.documentElement
      const accent = getComputedStyle(root).getPropertyValue('--bw-accent').trim()
      if (accent) setColor(shiftHue(accent))
    }
    read()
    const mo = new MutationObserver(read)
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'data-bw-motion'],
    })
    return () => mo.disconnect()
  }, [])

  return color
}
